import { useHtmlId } from '@ipfabric/frontend-shared-assets/utils/react/HtmlId';
import { FormikSelectFieldLegacy } from 'components/FormikSelectFieldLegacy/FormikSelectFieldLegacy';
import { useLocale } from 'containers/App/LocaleContext';
import { useFormik } from 'formik';
import { forwardRef, useImperativeHandle } from 'react';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import type { WebHookAction, WebHookId, WebHookType } from './WebHooksState';
import type { TestWebHookCommand } from './commands/testWebHook';
import { useTypeTranslator } from './helpers/useTypeTranslator';

type Options = {
  actions: Record<WebHookType, WebHookAction[]>;
  types: WebHookType[];
  // Trigger-based webhooks expose a per-`type/action` status list to test.
  withStatus: boolean;
  statusesByKey: Record<string, string[]>;
};

interface Props {
  options: Options;
  testWebHook: (command: TestWebHookCommand) => void;
  webhookId: WebHookId;
  webhookName: string;
}

export interface TestValues {
  action: WebHookAction;
  id: WebHookId;
  name: string;
  type: WebHookType;
  status?: string;
}

const statusKey = (type: WebHookType, action: WebHookAction) =>
  `${type}/${action}`;

export const TestForm = forwardRef(
  ({ options, testWebHook, webhookId, webhookName }: Props, ref) => {
    const msg = useLocale();
    const getTypeTranslation = useTypeTranslator();

    // Fixed noUncheckedIndexedAccess NIM-19408
    const initialType = options.types[0]!;
    const initialAction = options.actions[initialType]?.[0]!;
    const initialValues: TestValues = {
      action: initialAction,
      id: webhookId,
      name: webhookName,
      type: initialType,
      status: options.withStatus
        ? options.statusesByKey[statusKey(initialType, initialAction)]?.[0]
        : undefined,
    };

    const formik = useFormik({
      onSubmit: makeFormikOnSubmit((values: TestValues) => {
        testWebHook({ values });
      }),
      initialValues,
    });

    useImperativeHandle(ref, () => formik);
    const typeId = useHtmlId('type');
    const actionId = useHtmlId('action');
    const statusId = useHtmlId('status');

    const { values } = formik;

    // set new action on type change
    if (!(options.actions[values.type] ?? []).includes(values.action)) {
      formik.setFieldValue('action', (options.actions[values.type] ?? [])[0]);
    }

    // keep the status valid for the current type/action selection
    const currentStatuses = options.withStatus
      ? (options.statusesByKey[statusKey(values.type, values.action)] ?? [])
      : [];
    if (
      options.withStatus &&
      (!values.status || !currentStatuses.includes(values.status))
    ) {
      formik.setFieldValue('status', currentStatuses[0]);
    }

    return (
      <form onSubmit={formik.handleSubmit}>
        <div className='grid grid-cols-2 gap-8'>
          <div>
            <label htmlFor={typeId}>{msg.settings.webHooks.form.type}</label>
            <FormikSelectFieldLegacy
              id={typeId}
              options={options.types.map((value) => ({
                label: getTypeTranslation(value),
                value,
              }))}
              name='type'
              formik={formik}
            />
          </div>
          <div>
            <label htmlFor={actionId}>
              {msg.settings.webHooks.form.action}
            </label>
            <FormikSelectFieldLegacy
              id={actionId}
              options={(options.actions[values.type] ?? []).map((value) => ({
                label: value,
                value,
              }))}
              name='action'
              formik={formik}
            />
          </div>
        </div>
        {options.withStatus && (
          <div className='mt-4'>
            <label htmlFor={statusId}>
              {msg.settings.webHooks.form.status}
            </label>
            <FormikSelectFieldLegacy
              id={statusId}
              options={currentStatuses.map((value) => ({
                label: value,
                value,
              }))}
              name='status'
              formik={formik}
            />
          </div>
        )}
      </form>
    );
  },
);
